'use client';

import { useId } from 'react';
import { motion } from 'motion/react';
import type { Variants } from 'motion/react';
import { useTranslations } from 'next-intl';

import { dur, ease, spring, springFade, stagger } from '@/lib/motion';
import { hl } from '@/lib/rich-text';

/**
 * "Nereye gidiyoruz" — sayfanın son sözü, kapanış CTA'sının üstü.
 *
 * ── Neden yol haritası DEĞİL ──────────────────────────────────────────────
 *
 * Tarihli bir yol haritası bu sayfanın kendi cümlesiyle çelişirdi: bir önceki
 * bölüm "rakam vaat etmiyoruz" diyor, hemen ardından çeyrek çeyrek takvim
 * yayınlamak aynı sözün ihlali olurdu. Bu yüzden üç madde de YÖN söylüyor,
 * tarih söylemiyor. Buradaki tek somut bilgi (TikTok'un API onayı beklemesi)
 * anasayfada zaten yazılı.
 *
 * ── Neden ortalanmış ve tek sütun ─────────────────────────────────────────
 *
 * Sayfa buraya kadar sola dayalı ızgarayla aktı. Kapanış bilerek farklı bir
 * ölçüde duruyor: metin sütunu daralıyor, hizalama ortaya geliyor. Göz
 * "anlatı bitti" bilgisini içerikten önce yerleşimden alıyor. Aynı hareketi
 * kapanış CTA'sı da yapıyor, yani ikisi tek bir kapanış gibi okunuyor.
 *
 * ── Hareket ───────────────────────────────────────────────────────────────
 *
 * Başlık bloğu tek parça giriyor, ardından üç madde `stagger.base` ile.
 * Her maddenin üstündeki kısa yeşil işaret SOLDAN çekiliyor — sayfadaki
 * diğer yatay hatlarla aynı yön.
 *
 * Blur yalnızca üç metinde. Döngü ve parallax yok, kök layout'taki
 * <MotionConfig reducedMotion="user"> yetiyor.
 *
 * Giriş hareketi YOK (kullanıcı kararı, 9 Eyl 2026): bölüm sayfa açılır
 * açılmaz son hâlinde basılıyor (`initial={false}`). Eskiden `whileInView` +
 * `once: true` ile kaydırdıkça açılıyordu; site sahipleri sayfanın yüklenmiş
 * gelmesini istedi. Variant'lar yerinde — geri istenirse tetik tek satır.
 */

const DIRECTIONS = ['scope', 'depth', 'trust'] as const;

const sectionV: Variants = {
  hidden: {},
  show: { transition: { staggerChildren: stagger.base } },
};

const headV: Variants = {
  hidden: { opacity: 0, y: 10 },
  show: { opacity: 1, y: 0, transition: springFade(spring.default) },
};

const gridV: Variants = {
  hidden: {},
  show: { transition: { staggerChildren: stagger.base, delayChildren: stagger.tight } },
};

const cellV: Variants = {
  hidden: {},
  show: { transition: { staggerChildren: stagger.tight } },
};

const markV: Variants = {
  hidden: { opacity: 0, scaleX: 0 },
  show: {
    opacity: 1,
    scaleX: 1,
    transition: { duration: dur.base, ease: ease.out },
  },
};

const titleV: Variants = {
  hidden: { opacity: 0, y: 8 },
  show: { opacity: 1, y: 0, transition: springFade(spring.default) },
};

const textV: Variants = {
  hidden: { opacity: 0, y: 8, filter: 'blur(5px)' },
  show: {
    opacity: 1,
    y: 0,
    filter: 'blur(0px)',
    transition: springFade(spring.default, dur.base),
  },
};

export function AboutHorizon() {
  const t = useTranslations('about.horizon');
  const uid = useId();

  return (
    <section className="ahead" aria-labelledby={`${uid}-title`}>
      <div className="grid-12-container">
        <div className="grid-item-8fr">
          <motion.div className="ahead-inner" variants={sectionV} initial={false} animate="show">
            <motion.div className="ahead-head" variants={headV}>
              <p className="ahead-eyebrow">{t('eyebrow')}</p>
              <h2 className="ahead-title" id={`${uid}-title`}>
                {t.rich('title', { hl })}
              </h2>
              <p className="ahead-lead">{t('lead')}</p>
            </motion.div>

            <motion.ul className="ahead-grid" variants={gridV}>
              {DIRECTIONS.map((key) => (
                <motion.li className="ahead-cell" key={key} variants={cellV}>
                  <motion.span className="ahead-mark" aria-hidden="true" variants={markV} />
                  <motion.h3 className="ahead-name" variants={titleV}>
                    {t(`items.${key}.title`)}
                  </motion.h3>
                  <motion.p className="ahead-text" variants={textV}>
                    {t(`items.${key}.text`)}
                  </motion.p>
                </motion.li>
              ))}
            </motion.ul>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
